import './ResultsGrid.scss';

import * as React from 'react';

import { ActivityStage } from '@showbie-socrative/socrative-utils';
import { useQueryClient } from '@tanstack/react-query';

import { translate } from '~l10n/client';
import ToggleSwitch from '~shared/components/ToggleSwitch/ToggleSwitch';
import { ResultsStatsRow } from '~teacher/components/ResultsGrid/ResultsStatsRow';
import { useActivityTimers } from '~teacher/hook/activity/useActivityTimers';

import {
  useScoreDisplay,
  useShowNames,
  useShowResponses,
  useShowResults,
  useShowTimers,
} from './atoms';
import { Insights } from './Insights/Insights';
import {
  Participant,
  ResultsTable,
  ResultsTableQuestion,
} from './ResultsTable';
import { useStudentTimerStates } from './useStudentTimerStates';

interface Props {
  showInsights?: boolean;
  resultsTableQuestions?: ResultsTableQuestion[];
  participants?: Participant[];
  maxScore: number;
  activityInstanceId: number;
  activityStage: ActivityStage;
  isRosteredRoom: boolean;
  passRate: number | null;
  totalStudents?: number | null;
  presentStudents?: number;
  completionRate?: number;
  averageScoreRate?: number | null;
  passingStudentsRate?: number;
  /** Whether this quiz has timers configured (from backend). Controls data fetching and toggle visibility. */
  hasTimers?: boolean;
  quizTimerDurationSeconds?: number;
}

/**
 * Controls layout of Results view options and Results table.
 */
export function ResultsGrid({
  showInsights = false,
  resultsTableQuestions = [],
  participants = [],
  maxScore,
  activityInstanceId,
  activityStage,
  isRosteredRoom,
  passRate,
  totalStudents,
  presentStudents,
  completionRate,
  averageScoreRate,
  passingStudentsRate,
  hasTimers = false,
  quizTimerDurationSeconds: quizTimerDurationSecondsProp = 0,
}: Props): React.ReactElement {
  const isActivityInProgress = activityStage === ActivityStage.InProgress;

  const [showNames, setShowNames] = useShowNames(isActivityInProgress);
  const [showResponses, setShowResponses] =
    useShowResponses(isActivityInProgress);
  const [showResults, setShowResults] = useShowResults(isActivityInProgress);
  // User preference for timer visibility (persisted in localStorage). Only relevant when hasTimers is true.
  const [showTimers, setShowTimers] = useShowTimers(isActivityInProgress);

  const [scoreDisplay, setScoreDisplay] = useScoreDisplay(isActivityInProgress);
  const queryClient = useQueryClient();

  React.useEffect(() => {
    if (hasTimers && isActivityInProgress) {
      queryClient.invalidateQueries(['activity', 'timers', activityInstanceId]);
    }
  }, [hasTimers, isActivityInProgress, queryClient, activityInstanceId]);

  const timersQuery = useActivityTimers(activityInstanceId, hasTimers);
  // Two-stage memo: derive a stable string key first, then build the Set only
  // when actual membership changes. Without this, every StudentResponse PubNub
  // event produces a new participants array reference (via Immer + unmemoized
  // parseResultData), which creates a new Set reference, which re-triggers the
  // useStudentTimerStates effect and resets all NTP timer anchors.
  const finishedUuidsKey = React.useMemo(
    () =>
      participants
        .filter((p) => p.finished)
        .map((p) => p.uuid)
        .sort()
        .join(','),
    [participants]
  );
  const finishedStudentUuids = React.useMemo(
    () =>
      finishedUuidsKey
        ? new Set(finishedUuidsKey.split(','))
        : new Set<string>(),
    [finishedUuidsKey]
  );
  const timerStatesMap = useStudentTimerStates(
    timersQuery.data,
    finishedStudentUuids,
    !isActivityInProgress
  );

  const gridOptionsRef = React.useRef<HTMLDivElement>(null);

  const questionIdToNumberMap = new Map(
    resultsTableQuestions.map((question) => [
      `${question.questionId}`,
      question.order,
    ])
  );

  const participantUuidToNameMap = new Map(
    participants.map((p) => [`${p.uuid}`, p.name])
  );

  return (
    <>
      <div className="sticky left-0 w-screen -mr-8 legacy-container">
        {!isActivityInProgress && (
          <div className="flex flex-col gap-4">
            <Insights
              showInsights={showInsights}
              activityInstanceId={activityInstanceId}
              questionIdToNumberMap={questionIdToNumberMap}
              participantUuidToNameMap={participantUuidToNameMap}
              averageScoreRate={averageScoreRate}
            />
            <ResultsStatsRow
              activityInstanceId={activityInstanceId}
              passRate={passRate}
              totalStudents={totalStudents}
              presentStudents={presentStudents}
              completionRate={completionRate}
              averageScoreRate={averageScoreRate}
              passingStudentsRate={passingStudentsRate}
            />
          </div>
        )}
      </div>
      <div data-testid="results-grid" className="pb-12 results-grid">
        <div
          className="sticky top-0 left-0 z-30 -mr-8 bg-white w-screen legacy-container"
          ref={gridOptionsRef}
        >
          <div className="pt-6 pb-2 bg-white">
            <div className="flex flex-col gap-4 md:flex md:flex-row">
              <label className="flex items-center cursor-pointer">
                <ToggleSwitch
                  size="small"
                  on={showNames}
                  onChange={(checked) => {
                    setShowNames(checked);
                  }}
                />
                <span
                  className="pl-2 text-sm font-500 text-blue-900"
                  data-testid="show-names"
                >
                  {translate('Show Names')}
                </span>
              </label>
              <label className="flex items-center md:pl-3 cursor-pointer">
                <ToggleSwitch
                  size="small"
                  on={showResponses}
                  onChange={(checked) => {
                    setShowResponses(checked);
                  }}
                />
                <span
                  className="pl-2 text-sm font-500 text-blue-900"
                  data-testid="show-responses"
                >
                  {translate('Show Responses')}
                </span>
              </label>
              <label className="flex items-center md:pl-3 cursor-pointer">
                <ToggleSwitch
                  size="small"
                  on={showResults}
                  onChange={(checked) => {
                    setShowResults(checked);
                  }}
                />
                <span
                  className="pl-2 text-sm font-500 text-blue-900"
                  data-testid="show-results"
                >
                  {translate('Show Results')}
                </span>
              </label>
              {hasTimers && (
                <label className="flex items-center md:pl-3 cursor-pointer">
                  <ToggleSwitch
                    size="small"
                    on={showTimers}
                    onChange={(checked) => {
                      setShowTimers(checked);
                    }}
                  />
                  <span
                    className="pl-2 text-sm font-500 text-blue-900"
                    data-testid="show-timers"
                  >
                    {translate('Show Timers')}
                  </span>
                </label>
              )}
            </div>
          </div>
        </div>
        <ResultsTable
          isRosteredRoom={isRosteredRoom}
          isActivityInProgress={isActivityInProgress}
          showNames={showNames}
          showResults={showResults}
          showResponses={showResponses}
          hasTimers={hasTimers}
          showTimers={hasTimers && showTimers}
          timerStatesMap={timerStatesMap}
          quizTimerDurationSeconds={quizTimerDurationSecondsProp}
          scoreDisplay={scoreDisplay}
          setScoreDisplay={setScoreDisplay}
          resultsTableQuestions={resultsTableQuestions}
          participants={participants}
          maxScore={maxScore}
          activityInstanceId={activityInstanceId}
          optionsContainer={gridOptionsRef}
        />
      </div>
    </>
  );
}
